import React, { useEffect, useState } from 'react';

export type RequestFlow = {
  submitted: number;   // کل درخواست‌های ثبت شده
  approved: number;    // درخواست‌های تایید شده توسط ایمنی
  completed: number;   // درخواست‌های تکمیل شده توسط تعمیرات
  remaining: number;   // درخواست‌های باقی مانده
};

type Props = {
  colors: any;
  buildUrlWithFilters: (base: string) => string;   // kept in case you need GET endpoints elsewhere
  buildPostBody?: () => any;                       // 🔸 parent passes RFC3339 start/end (+ filters)
};

// Persian digits helper
const toFaDigits = (v: unknown): string => {
  if (v === null || v === undefined) return '';
  return String(v)
    .replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)])
    .replace(/\./g, '٫')
    .replace(/,/g, '٬');
};

const RequestToExecutionCard: React.FC<Props> = ({ colors, buildUrlWithFilters, buildPostBody }) => {
  const [data, setData] = useState<RequestFlow>({ submitted: 0, approved: 0, completed: 0, remaining: 0 });
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const card: React.CSSProperties = {
    background: colors.panel,
    border: `1px solid ${colors.border}`,
    borderRadius: 16,
    padding: 16,
    boxShadow: '0 6px 20px rgba(0,0,0,.25)',
  };

  const title: React.CSSProperties = { fontWeight: 800, marginBottom: 8, letterSpacing: 0.2 };
  const hint: React.CSSProperties = { fontSize: 12, color: colors.sub, marginBottom: 10 };

  const row: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '86px 1fr 64px',
    alignItems: 'center',
    gap: 10,
  };

  const label: React.CSSProperties = { fontSize: 12, color: colors.sub, textAlign: 'right', whiteSpace: 'nowrap' };
  const barWrap: React.CSSProperties = {
    height: 12,
    background: '#0b1220',
    border: `1px solid ${colors.border}`,
    borderRadius: 999,
    overflow: 'hidden',
  };
  const bar = (wPct: number, bg: string): React.CSSProperties => ({
    width: `${Math.max(0, Math.min(100, wPct))}%`,
    height: '100%',
    background: bg,
    transition: 'width .35s ease',
  });
  const val: React.CSSProperties = { fontSize: 12, fontWeight: 800, textAlign: 'left' };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setErr(null);

      const token = localStorage.getItem('token') || '';
      const headers: HeadersInit = {
        Accept: 'application/json',
        'Content-Type': 'application/json',            // ✅ needed for POST JSON
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      // 🔹 Your backend: POST /api/dashboard/operation/count with JSON body {start, end, ...}
      const endpoint = '/api/dashboard/operation/count';
      const body = buildPostBody ? buildPostBody() : {};

      try {
        const res = await fetch(endpoint, {
          method: 'POST',
          headers,
          credentials: 'include',
          body: JSON.stringify(body),
        });

        const text = await res.text();

        if (!res.ok) {
          // graceful fallback so UI stays useful
          setData({ submitted: 280, approved: 220, completed: 150, remaining: 70 });
          setErr(`HTTP ${res.status}: ${text || 'server error'}`);
          return;
        }

        const j = text ? JSON.parse(text) : {};

        // Backend fields (from your Postman screenshot):
        // total_operations
        // total_operation_approved
        // total_operation_rejected
        // total_operation_finished
        // total_operation_granted_spade
        // total_operation_waiting_for_spades
        const submitted = Number(j?.total_operations ?? 0);
        const approved  = Number(j?.total_operation_approved ?? 0);
        const completed = Number(j?.total_operation_finished ?? 0);

        // Prefer explicit waiting count; otherwise compute remaining = total - finished
        const waiting    = Number(j?.total_operation_waiting_for_spades);
        const remaining  = Number.isFinite(waiting) ? waiting : Math.max(0, submitted - completed);

        setData({ submitted, approved, completed, remaining });
      } catch (e: any) {
        setData({ submitted: 280, approved: 220, completed: 150, remaining: 70 });
        setErr(e?.message || 'خطای دریافت داده');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [buildPostBody, buildUrlWithFilters]);

  // bar lengths (percent of submitted)
  const base = data.submitted || 1;
  const pApproved = (data.approved / base) * 100;
  const pCompleted = (data.completed / base) * 100;
  const pRemaining = (data.remaining / base) * 100;

  return (
    <section style={card} dir="rtl">
      <div style={title}>نمودار درخواست تا اجرا</div>
      <div style={hint}>وضعیت فرآیند: ثبت تا تایید ایمنی، اجرا و باقی‌مانده</div>
      {loading && <div style={{ ...hint, color: colors.accent }}>در حال بارگذاری…</div>}
      {err && <div style={{ ...hint, color: colors.danger }}>{err}</div>}

      <div style={{ display: 'grid', gap: 12 }}>
        {/* کل ثبت شده */}
        <div style={row}>
          <div style={label}>کل ثبت‌شده</div>
          <div style={barWrap}>
            <div style={bar(100, 'rgba(96,165,250,0.75)')} />
          </div>
          <div style={val}>{toFaDigits(data.submitted.toLocaleString('fa-IR'))}</div>
        </div>

        {/* تایید ایمنی */}
        <div style={row}>
          <div style={label}>تایید ایمنی</div>
          <div style={barWrap}>
            <div style={bar(pApproved, 'rgba(34,211,238,0.8)')} />
          </div>
          <div style={val}>{toFaDigits(data.approved.toLocaleString('fa-IR'))}</div>
        </div>

        {/* تکمیل توسط تعمیرات */}
        <div style={row}>
          <div style={label}>تکمیل تعمیرات</div>
          <div style={barWrap}>
            <div style={bar(pCompleted, 'rgba(52,211,153,0.85)')} />
          </div>
          <div style={val}>{toFaDigits(data.completed.toLocaleString('fa-IR'))}</div>
        </div>

        {/* باقی مانده */}
        <div style={row}>
          <div style={label}>باقی‌مانده</div>
          <div style={barWrap}>
            <div style={bar(pRemaining, 'rgba(248,113,113,0.85)')} />
          </div>
          <div style={val}>{toFaDigits(data.remaining.toLocaleString('fa-IR'))}</div>
        </div>
      </div>

      <div style={{ marginTop: 10, fontSize: 11, color: colors.sub }}>
        * نوارها نسبت به «کل ثبت‌شده» مقیاس‌دهی شده‌اند.
      </div>
    </section>
  );
};

export default RequestToExecutionCard;
